// Grimoire / Chronicle — story timeline ("diary") of the current game.
//
// Iter 103 — Removed "events" and "threads" sub-views per user request:
// they didn't have a clear in-game purpose. Only the diary (page-by-page
// retrospect with chapter grouping + alt-paths exploration) remains.
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  ScrollText, Crown, Loader2, BookOpen, Skull, Check, ChevronRight,
  Sparkles,
} from "lucide-react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import useTabMusic from "@/hooks/useTabMusic";
import { t } from "@/utils/i18n";

const RARITY_COLORS = {
  common: "#cbd5e1",
  uncommon: "#34d399",
  rare: "#60a5fa",
  epic: "#a78bfa",
  legendary: "#fbbf24",
};


export default function Grimoire() {
  const { game } = useGame();
  const { token, API } = useAuth();
  useTabMusic('grimoire');
  // Iter 103 — Removed "events" and "threads" sub-views: the player asked to
  // drop sections without a clear purpose. Only the "Diario" (story timeline)
  // is meaningful and remains. The variable is kept as a literal for clarity.
  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");
  // Diary (story timeline)
  const [diary, setDiary] = useState([]);
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [diaryError, setDiaryError] = useState(null);

  const fetchDiary = useCallback(async () => {
    if (!game?.id || !token) return;
    setDiaryLoading(true);
    setDiaryError(null);
    try {
      const r = await axios.get(`${API}/game/${game.id}/story/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiary(r.data.timeline || []);
    } catch (e) {
      setDiaryError(e?.response?.data?.detail || "Failed to load timeline");
    } finally {
      setDiaryLoading(false);
    }
  }, [game?.id, token, API]);

  useEffect(() => {
    fetchDiary();
  }, [fetchDiary]);

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4 py-4" data-testid="grimoire">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ScrollText size={22} className="text-[#cfb53b] shrink-0" />
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl text-[#cfb53b] truncate">
              {isIt ? "Grimorio" : "Chronicle"}
            </h1>
            <p className="text-[11px] text-[#64748b]">
              {diary.length} {isIt ? "pagine narrate" : "pages narrated"}
            </p>
          </div>
        </div>
      </div>

      {/* Diary view (the only remaining view) */}
      <div data-testid="grimoire-diary">
        {diaryLoading && (
          <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-[#cfb53b]" /></div>
        )}
        {diaryError && (
          <div className="bg-red-950/40 border border-red-700/50 text-red-300 px-3 py-2 rounded-md text-xs mb-3">
            {diaryError}
          </div>
        )}
        {!diaryLoading && !diaryError && diary.length === 0 && (
          <div className="text-center py-12 text-[#64748b]">
            <BookOpen size={36} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">{isIt ? "Il tuo diario è vuoto. Inizia la storia!" : "Your diary is empty. Begin the story!"}</p>
          </div>
        )}
        {!diaryLoading && diary.length > 0 && (
          <DiaryList diary={diary} isIt={isIt} gameId={game?.id} token={token} API={API} />
        )}
      </div>
    </div>
  );
}

// ============ Diary sub-component ============
function DiaryList({ diary, isIt, gameId, token, API }) {
  // Group pages by chapter
  const chapters = {};
  diary.forEach((p) => {
    const ch = p.chapter || 1;
    if (!chapters[ch]) chapters[ch] = [];
    chapters[ch].push(p);
  });
  const chapterNums = Object.keys(chapters).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-5" data-testid="diary-list">
      {chapterNums.map((ch) => (
        <div key={ch} data-testid={`diary-chapter-${ch}`}>
          <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#0a0a0c]/95 backdrop-blur-sm py-1.5 z-10">
            <BookOpen size={14} className="text-[#a78bfa] shrink-0" />
            <h2 className="font-heading text-sm text-[#a78bfa]">
              {isIt ? "Capitolo" : "Chapter"} {ch}
            </h2>
            <div className="flex-1 h-px bg-[#a78bfa]/20" />
            <span className="text-[10px] text-[#64748b]">
              {chapters[ch].length} {isIt ? "pagine" : "pages"}
            </span>
          </div>
          <ol className="space-y-2 pl-1">
            {chapters[ch].map((p) => (
              <DiaryEntry key={p.page_index} entry={p} isIt={isIt} gameId={gameId} token={token} API={API} />
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function DiaryEntry({ entry, isIt, gameId, token, API }) {
  const isBoss = entry.is_boss;
  const isBattle = entry.is_battle && !isBoss;
  // Iter 81 — Alt paths state. We lazy-load on first click to avoid hammering
  // the API for every Diary entry.
  const [altOpen, setAltOpen] = React.useState(false);
  const [altLoading, setAltLoading] = React.useState(false);
  const [branches, setBranches] = React.useState(null);

  const loadBranches = React.useCallback(async () => {
    if (branches !== null) return; // already loaded
    setAltLoading(true);
    try {
      const { default: axios } = await import("axios");
      const res = await axios.get(
        `${API}/game/${gameId}/page/${entry.page_number}/branches`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBranches(res.data?.branches || []);
    } catch {
      setBranches([]);
    } finally {
      setAltLoading(false);
    }
  }, [API, gameId, token, entry.page_number, branches]);

  const altCount = branches ? branches.filter((b) => !b.taken).length : null;
  // Show the badge optimistically when the entry has > 1 choice (likely has alternates)
  const probablyHasAlts = (entry.choices?.length || 0) > 1;

  return (
    <li
      className="bg-[#1a1a1d] border-l-2 rounded-r-md px-3 py-2 hover:bg-[#1a1a1d]/80 transition-colors"
      style={{
        borderLeftColor: isBoss ? "#a78bfa" : isBattle ? "#f87171" : "#cfb53b",
      }}
      data-testid={`diary-entry-${entry.page_index}`}
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-[10px] font-mono text-[#64748b] shrink-0">
          {String(entry.page_number).padStart(2, "0")}
        </span>
        {isBoss && <Crown size={11} className="text-[#a78bfa] shrink-0" />}
        {isBattle && <Skull size={11} className="text-[#f87171] shrink-0" />}
        <p className="text-xs text-[#e2e8f0] leading-relaxed flex-1">
          {entry.preview || (isIt ? "Pagina vuota" : "Blank page")}
        </p>
      </div>

      {/* Choices */}
      {entry.choices?.length > 0 && (
        <ul className="mt-2 ml-3 pl-2 border-l border-[#2d3748] space-y-1">
          {entry.choices.map((choiceText, i) => {
            const isChosen = entry.chosen && entry.chosen === choiceText;
            return (
              <li
                key={i}
                className={`text-[11px] flex items-start gap-1.5 ${
                  isChosen ? "text-emerald-300" : "text-[#64748b] italic line-through opacity-60"
                }`}
                data-testid={`diary-choice-${entry.page_index}-${i}${isChosen ? "-chosen" : ""}`}
              >
                {isChosen
                  ? <Check size={10} className="text-emerald-400 mt-0.5 shrink-0" />
                  : <ChevronRight size={10} className="mt-0.5 shrink-0" />}
                <span className="leading-snug">
                  {choiceText.length > 110 ? choiceText.slice(0, 110) + "…" : choiceText}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Alternate paths trigger */}
      {probablyHasAlts && (
        <button
          type="button"
          onClick={() => { setAltOpen(true); loadBranches(); }}
          data-testid={`diary-alt-btn-${entry.page_index}`}
          className="mt-2 ml-3 inline-flex items-center gap-1 text-[10px] font-heading text-amber-300/80 hover:text-amber-300 transition-colors"
        >
          <Sparkles size={10} />
          {altCount === null
            ? (isIt ? "Sentieri alternativi" : "Alternate paths")
            : altCount === 0
              ? (isIt ? "Nessun sentiero registrato" : "No alternates recorded")
              : (isIt ? `${altCount} sentiero${altCount === 1 ? "" : "i"} alternativ${altCount === 1 ? "o" : "i"}` : `${altCount} alternate path${altCount === 1 ? "" : "s"}`)}
        </button>
      )}

      <AlternatePathsDialog
        open={altOpen}
        onClose={() => setAltOpen(false)}
        loading={altLoading}
        branches={branches}
        chosenText={entry.chosen}
        pageNumber={entry.page_number}
        isIt={isIt}
      />
    </li>
  );
}

function AlternatePathsDialog({ open, onClose, loading, branches, chosenText, pageNumber, isIt }) {
  if (!open) return null;
  const list = branches || [];
  const alternates = list.filter((b) => !b.taken);
  return (
    <div
      data-testid={`diary-alt-dialog-${pageNumber}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0a0a0c] border border-amber-500/40 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0a0a0c] border-b border-amber-500/30 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={16} className="text-amber-300 shrink-0" />
            <h3 className="font-heading text-sm text-amber-300 truncate">
              {isIt ? `Sentieri Alternativi — Pagina ${pageNumber}` : `Alternate Paths — Page ${pageNumber}`}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="diary-alt-close"
            className="text-[#94a3b8] hover:text-[#e2e8f0] text-xl leading-none px-2"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* What you actually chose — preserved for context */}
          {chosenText && (
            <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Check size={11} className="text-emerald-400" />
                <span className="text-[10px] font-heading text-emerald-300 uppercase tracking-wider">
                  {isIt ? "Hai scelto" : "You chose"}
                </span>
              </div>
              <p className="text-xs text-emerald-200 italic leading-snug">{chosenText}</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-6 text-[#94a3b8] text-xs">
              <Loader2 size={20} className="mx-auto mb-2 animate-spin text-amber-400" />
              {isIt ? "Sto rivelando i sentieri..." : "Revealing the paths..."}
            </div>
          )}

          {!loading && branches !== null && alternates.length === 0 && (
            <div className="text-center py-8 text-[#64748b] text-xs italic">
              {isIt
                ? "Per questa pagina nessun sentiero alternativo è stato registrato. Forse l'AI era distratta dal destino."
                : "No alternate paths were recorded for this page. Perhaps the AI was distracted by fate."}
            </div>
          )}

          {!loading && alternates.length > 0 && (
            <>
              <p className="text-[11px] text-amber-400/80 italic">
                {isIt
                  ? "Cosa sarebbe successo se avessi scelto diversamente:"
                  : "What would have happened if you had chosen differently:"}
              </p>
              {alternates.map((b) => (
                <div
                  key={b.choice_id}
                  data-testid={`diary-alt-${pageNumber}-${b.choice_id}`}
                  className="bg-[#1a1a1d] border border-amber-500/20 rounded-md overflow-hidden"
                >
                  {b.image_url && (
                    <div className="relative h-28 sm:h-36 overflow-hidden border-b border-amber-500/15">
                      <img
                        src={b.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-3 space-y-1.5">
                    <div className="flex items-start gap-1.5">
                      <ChevronRight size={11} className="text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-xs font-heading text-amber-200 leading-snug">
                        {b.choice_text}
                      </p>
                    </div>
                    <p className="text-[11px] text-[#cbd5e1] leading-relaxed pl-4 italic">
                      {b.story_text || (isIt ? "(Nessun testo registrato)" : "(No text recorded)")}
                    </p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * ChapterEndingsList — Iter 73.
 *
 * Fetches and renders the poetic titles assigned to the player's closed
 * chapters (Endings Tracker). Used as the HEAD of the Threads tab so the
 * chapter arc reads naturally before the per-flag detail below.
 */

/**
 * ThreadsPanel — Story Flags view (iter 72).
 *
 * Displays the `game.story_flags` dict as a list of "plot threads" — past
 * choices the player made that the AI is expected to keep honoring. Purely
 * informational: the player can't modify these; they're written by the AI
 * on consequential choices.
 */

